import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { AccessControlService, AccessPerson, AccessUser, AccessUserUpdate } from '../services/access-control.service';
import { PeopleService, GraphPerson, PeopleSearchField } from '../services/people.service';

@Component({
  selector: 'app-access-control',
  standalone: false,
  templateUrl: './access-control.component.html',
  styleUrl: './access-control.component.scss'
})
export class AccessControlComponent implements OnInit, OnDestroy {
  readonly searchFields: { value: PeopleSearchField; label: string }[] = [
    { value: 'displayName', label: 'Display name' },
    { value: 'gmid', label: 'GMID' },
    { value: 'email', label: 'Email' },
    { value: 'org', label: 'Organization' }
  ];

  searchField: PeopleSearchField = 'displayName';
  searchTerm = '';
  searchResults: GraphPerson[] = [];
  selectedPeople = new Map<string, GraphPerson>();
  accessUsers: AccessUser[] = [];
  userPhotos = new Map<string, string>();
  roleDrafts = new Map<number, string>();
  updatingUserIds = new Set<number>();
  searching = false;
  loadingAccessUsers = false;
  saving = false;
  searchError = '';
  accessError = '';
  saveMessage = '';

  private readonly searchTerms$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly peopleService: PeopleService,
    private readonly accessControlService: AccessControlService
  ) {}

  ngOnInit(): void {
    this.loadAccessUsers();
    this.searchTerms$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(searchKey => {
        const [field, ...termParts] = searchKey.split('|');
        const term = termParts.join('|');
        if (term.length < 2) {
          this.searching = false;
          this.searchError = '';
          return of([] as GraphPerson[]);
        }

        this.searching = true;
        this.searchError = '';
        return this.peopleService.searchPeople(field as PeopleSearchField, term).pipe(
          catchError(() => {
            this.searchError = 'People search is unavailable. Try again.';
            return of([] as GraphPerson[]);
          }),
          finalize(() => this.searching = false)
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(results => {
      this.searchResults = results;
      this.loadPhotos(results.map(person => ({
        gmid: this.getGmid(person),
        userId: person.id || person.userPrincipalName || person.email || person.mail
      })));
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchTermChange(term: string): void {
    this.searchTerm = term;
    this.saveMessage = '';
    this.searchTerms$.next(`${this.searchField}|${term.trim()}`);
  }

  onSearchFieldChange(): void {
    this.searchTerms$.next(`${this.searchField}|${this.searchTerm.trim()}`);
  }

  togglePerson(person: GraphPerson): void {
    const gmid = this.getGmid(person);
    if (!gmid || this.isAlreadyAdded(gmid)) {
      return;
    }

    if (this.selectedPeople.has(gmid)) {
      this.selectedPeople.delete(gmid);
    } else {
      this.selectedPeople.set(gmid, person);
    }
  }

  isSelected(person: GraphPerson): boolean {
    const gmid = this.getGmid(person);
    return !!gmid && this.selectedPeople.has(gmid);
  }

  isAlreadyAdded(gmid: string): boolean {
    const normalizedGmid = gmid.trim().toLowerCase();
    return this.accessUsers.some(user =>
      Boolean(user.isActive) && user.gmid?.trim().toLowerCase() === normalizedGmid
    );
  }

  getGmid(person: GraphPerson): string {
    return (person.gmid || person.onPremisesSamAccountName || '').trim().toLowerCase();
  }

  getEmail(person: GraphPerson): string {
    return person.email || person.mail || person.userPrincipalName || 'No email available';
  }

  photoForGmid(gmid: string): string | null {
    return this.userPhotos.get(gmid.trim().toLowerCase()) ?? null;
  }

  addSelectedUsers(): void {
    const people: AccessPerson[] = Array.from(this.selectedPeople.values())
      .map(person => ({
        gmid: this.getGmid(person),
        email: person.email || person.mail || person.userPrincipalName || undefined
      }))
      .filter(person => !!person.gmid);
    if (!people.length || this.saving) {
      return;
    }

    this.saving = true;
    this.accessError = '';
    this.saveMessage = '';
    this.accessControlService.addAccessUsers(people).pipe(
      finalize(() => this.saving = false)
    ).subscribe({
      next: () => {
        this.saveMessage = `${people.length} user${people.length === 1 ? '' : 's'} added with access role.`;
        this.selectedPeople.clear();
        this.loadAccessUsers();
      },
      error: () => this.accessError = 'The selected users could not be saved. Try again.'
    });
  }

  trackByGmid = (_: number, person: GraphPerson): string => this.getGmid(person);
  trackByAccessId = (_: number, user: AccessUser): number => user.id;

  getRoleDraft(user: AccessUser): string {
    return this.roleDrafts.get(user.id) ?? user.userRole;
  }

  setRoleDraft(user: AccessUser, role: string): void {
    this.roleDrafts.set(user.id, role.trim());
  }

  isRoleChanged(user: AccessUser): boolean {
    return this.getRoleDraft(user) !== user.userRole;
  }

  isUpdatingUser(user: AccessUser): boolean {
    return this.updatingUserIds.has(user.id);
  }

  saveUserRole(user: AccessUser): void {
    const userRole = this.getRoleDraft(user);
    if (!userRole || !this.isRoleChanged(user) || this.isUpdatingUser(user)) {
      return;
    }

    this.updateUser({
      id: user.id,
      gmid: user.gmid,
      userRole,
      email: user.email ?? null,
      isActive: Boolean(user.isActive)
    });
  }

  setUserActive(user: AccessUser, isActive: boolean): void {
    if (this.isUpdatingUser(user)) {
      return;
    }

    if (isActive) {
      this.updateUser({
        id: user.id,
        gmid: user.gmid,
        userRole: user.userRole,
        email: user.email ?? null,
        isActive
      });
      return;
    }

    this.updatingUserIds = new Set(this.updatingUserIds).add(user.id);
    this.accessError = '';
    this.saveMessage = '';
    this.accessControlService.deleteAccessUsers([user.id]).pipe(
      finalize(() => this.finishUpdatingUser(user.id))
    ).subscribe({
      next: () => {
        this.saveMessage = `${user.gmid} access deactivated.`;
        this.loadAccessUsers();
      },
      error: () => this.accessError = `Could not deactivate ${user.gmid}. Try again.`
    });
  }

  private updateUser(user: AccessUserUpdate): void {
    this.updatingUserIds = new Set(this.updatingUserIds).add(user.id);
    this.accessError = '';
    this.saveMessage = '';
    this.accessControlService.updateAccessUsers([user]).pipe(
      finalize(() => this.finishUpdatingUser(user.id))
    ).subscribe({
      next: () => {
        this.saveMessage = `${user.gmid} updated.`;
        this.loadAccessUsers();
      },
      error: () => this.accessError = `Could not update ${user.gmid}. Try again.`
    });
  }

  private finishUpdatingUser(id: number): void {
    const updating = new Set(this.updatingUserIds);
    updating.delete(id);
    this.updatingUserIds = updating;
  }

  private loadPhotos(people: Array<{ gmid: string; userId?: string; lookupByGmid?: boolean }>): void {
    const requests = people
      .filter(person => person.gmid && (person.userId || person.lookupByGmid) && !this.userPhotos.has(person.gmid))
      .map(person => (person.lookupByGmid
        ? this.peopleService.getPhotoByGmid(person.gmid)
        : this.peopleService.getPhotoByUserId(person.userId!)).pipe(
        map(photo => ({ gmid: person.gmid, photo }))
      ));

    if (!requests.length) {
      return;
    }

    forkJoin(requests).pipe(takeUntil(this.destroy$)).subscribe(results => {
      const photos = new Map(this.userPhotos);
      results.forEach(({ gmid, photo }) => {
        if (photo) {
          photos.set(gmid, photo);
        }
      });
      this.userPhotos = photos;
    });
  }

  private loadAccessUsers(): void {
    this.loadingAccessUsers = true;
    this.accessError = '';
    this.accessControlService.getAccessUsers().pipe(
      finalize(() => this.loadingAccessUsers = false)
    ).subscribe({
      next: users => {
        this.accessUsers = users;
        this.roleDrafts = new Map(users.map(user => [user.id, user.userRole]));
        this.loadPhotos(users.map(user => ({
          gmid: user.gmid.trim().toLowerCase(),
          lookupByGmid: true
        })));
      },
      error: () => this.accessError = 'The access list could not be loaded.'
    });
  }
}
